/**
 * ════════════════════════════════════════════════════════════════
 * PartnerOffers — Gestion des Offres Marketing (Partenaire)
 * ════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURE: Offers = couche marketing uniquement.
 * Les champs commerciaux sont informatifs (pas de calcul de prix).
 * Le pricing réel est géré par ORU (promotional_mechanics).
 *
 * Voir: OFFERS_ARCHITECTURE.md §2
 * ════════════════════════════════════════════════════════════════
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Percent, Truck, Gift, Layers, Calendar,
  Search, Eye, EyeOff, Loader2, ChevronDown, ChevronUp, Image as ImageIcon, Package, X
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import GuardedDialog from '../../components/GuardedDialog';
import useFormDirtyState from '../../hooks/useFormDirtyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import DashboardWrapper from '../../components/DashboardWrapper';
import ImageUploadField from '../../components/ImageUploadField';

/**
 * PartnerOffers V2 - Gestion des offres partenaire
 * 
 * Refonte avec:
 * - Formulaire unifié (tous les champs Offer)
 * - Affichage en tableau
 * - Support image/badge pour CMS
 * - Responsive design
 */
const OFFER_TYPES = [
  { value: 'remise', label: 'Remise (%)', icon: Percent, color: 'bg-green-100 text-green-800' },
  { value: 'franco', label: 'Franco (€)', icon: Truck, color: 'bg-blue-100 text-blue-800' },
  { value: 'goodies', label: 'Goodies', icon: Gift, color: 'bg-purple-100 text-purple-800' },
  { value: 'gratuite', label: 'Gratuité', icon: Gift, color: 'bg-pink-100 text-pink-800' },
  { value: 'mixte', label: 'Mixte', icon: Layers, color: 'bg-orange-100 text-orange-800' },
];

// Status badges avec code couleur
const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  draft: { label: 'Brouillon', color: 'bg-orange-100 text-orange-800' },
  archived: { label: 'Archivée', color: 'bg-red-100 text-red-800' }
};

// Unités de mesure pour commandes
const ORDER_UNITS = [
  { value: '€', label: '€' },
  { value: 'unité', label: 'unité' }
];

const PartnerOffers = () => {
  const { user, activeOrg } = useAuth();
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { hasUnsavedChanges, setDirty, resetDirty } = useFormDirtyState();
  
  // Form state - Complet Offer V2
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'remise',
    discount_percent: '',
    franco_threshold: '',
    goodies_description: '',
    gratuite_description: '',  // Nouveau: texte libre pour gratuité
    minimum_order: '',
    maximum_order: '',
    multiple_order: '',
    min_order_unit: '€',  // Nouveau: unité pour min
    max_order_unit: '€',  // Nouveau: unité pour max
    start_date: '',
    end_date: '',
    is_active: true,
    status: 'active',
    // Champs CMS
    image_url: '',
    badge_text: '',
    badge_color: '#ef4444',
    offer_url: '',  // Nouveau: lien vers l'offre
    // Champs Produits
    product_ids: [],
    apply_to_all_products: true
  });
  
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    params: true,
    dates: true,
    display: false,
    products: true
  });

  // Phase 4.5b — scope par l'organisation active
  const partnerId = activeOrg?.org_id;

  useEffect(() => {
    const controller = new AbortController();
    if (partnerId) {
      loadOffers(controller.signal);
      loadProducts(controller.signal);
    } else {
      setOffers([]);
      setProducts([]);
      setLoading(false);
    }
    // Fermer le formulaire si l'org change (évite produits de l'org précédente dans le form)
    setShowForm(false);
    setEditingOffer(null);
    return () => controller.abort();
  }, [partnerId]);

  const loadOffers = async (signal) => {
    try {
      if (!partnerId) return;
      const response = await api.get(`/offers?partner_id=${partnerId}`, { signal });
      setOffers(response.data || []);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      toast.error('Erreur lors du chargement des offres');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async (signal) => {
    try {
      if (!partnerId) return;
      const response = await api.get(`/products?partner_id=${partnerId}&limit=200`, { signal });
      const rawProd = response.data || [];
      setProducts(Array.isArray(rawProd) ? rawProd : (rawProd?.products || []));
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Erreur chargement produits:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      offer_code: '',
      title: '',
      description: '',
      type: 'remise',
      discount_percent: '',
      franco_threshold: '',
      goodies_description: '',
      gratuite_description: '',
      minimum_order: '',
      maximum_order: '',
      multiple_order: '',
      min_order_unit: '€',
      max_order_unit: '€',
      start_date: '',
      end_date: '',
      is_active: true,
      status: 'active',
      image_url: '',
      badge_text: '',
      badge_color: '#ef4444',
      offer_url: '',
      product_ids: [],
      apply_to_all_products: true
    });
    setProductSearch('');
  };

  const openCreateDialog = () => {
    setEditingOffer(null);
    resetForm();
    setShowForm(true);
    resetDirty();
  };

  const openEditDialog = (offer) => {
    setEditingOffer(offer);
    setFormData({
      offer_code: offer.offer_code || '',
      title: offer.title || '',
      description: offer.description || '',
      type: offer.type || 'remise',
      discount_percent: offer.discount_percent || '',
      franco_threshold: offer.franco_threshold || '',
      goodies_description: offer.goodies_description || '',
      gratuite_description: offer.gratuite_description || '',
      minimum_order: offer.minimum_order || '',
      maximum_order: offer.maximum_order || '',
      multiple_order: offer.multiple_order || '',
      min_order_unit: offer.min_order_unit || '€',
      max_order_unit: offer.max_order_unit || '€',
      start_date: offer.start_date || '',
      end_date: offer.end_date || '',
      is_active: offer.is_active !== false,
      status: offer.status || 'active',
      image_url: offer.image_url || '',
      badge_text: offer.badge_text || '',
      badge_color: offer.badge_color || '#ef4444',
      offer_url: offer.offer_url || '',
      product_ids: offer.product_ids || [],
      apply_to_all_products: offer.apply_to_all_products !== false
    });
    setProductSearch('');
    setShowForm(true);
    resetDirty();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    if (!editingOffer && !formData.offer_code.trim()) {
      toast.error('Le code offre est obligatoire');
      return;
    }
    
    setSaving(true);
    
    try {
      const payload = {
        offer_code: editingOffer ? undefined : formData.offer_code.trim(),
        // Phase 4.5b: envoie activeOrg.org_id — le backend valide via resolve_write_org_id
        partner_id: partnerId || undefined,
        title: formData.title,
        description: formData.description,
        type: formData.type,
        discount_percent: formData.discount_percent ? parseFloat(formData.discount_percent) : null,
        franco_threshold: formData.franco_threshold ? parseFloat(formData.franco_threshold) : null,
        goodies_description: formData.goodies_description || null,
        minimum_order: formData.minimum_order ? parseInt(formData.minimum_order) : null,
        maximum_order: formData.maximum_order ? parseInt(formData.maximum_order) : null,
        multiple_order: formData.multiple_order ? parseInt(formData.multiple_order) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        is_active: formData.is_active,
        status: formData.status,
        image_url: formData.image_url || null,
        badge_text: formData.badge_text || null,
        badge_color: formData.badge_color || '#ef4444',
        product_ids: formData.apply_to_all_products ? [] : formData.product_ids,
        apply_to_all_products: formData.apply_to_all_products
      };

      if (editingOffer) {
        await api.put(`/offers/${editingOffer.id}`, payload);
        toast.success('Offre mise à jour');
      } else {
        await api.post('/offers', payload);
        toast.success('Offre créée');
      }

      setShowForm(false);
      resetDirty();
      setEditingOffer(null);
      resetForm();
      loadOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/offers/${deleteConfirm.id}`);
      toast.success('Offre supprimée');
      setDeleteConfirm(null);
      loadOffers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getTypeInfo = (type) => OFFER_TYPES.find(t => t.value === type) || OFFER_TYPES[0];

  // Filtrage local
  const filteredOffers = offers.filter(offer =>
    offer.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    offer.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const stats = {
    total: offers.length,
    active: offers.filter(o => o.is_active && o.status === 'active').length,
    draft: offers.filter(o => o.status === 'draft').length
  };

  return (
    <DashboardWrapper>
      <div className="space-y-6 max-w-full overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold">Mes Offres</h1>
            <p className="text-muted-foreground">
              {stats.total} offre(s) • {stats.active} active(s)
            </p>
          </div>
          <Button onClick={openCreateDialog} data-testid="create-offer-button" className="shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            Créer une offre
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
              <div className="text-sm text-muted-foreground">Actives</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-500">{stats.draft}</div>
              <div className="text-sm text-muted-foreground">Brouillons</div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher une offre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-offers-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Offers List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredOffers.length === 0 ? (
              <div className="text-center py-12">
                <Percent className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? 'Aucune offre trouvée' : 'Aucune offre pour le moment'}
                </p>

              </div>
            ) : (
              <div className="divide-y">
                {filteredOffers.map((offer) => {
                  const typeInfo = getTypeInfo(offer.type);
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <div 
                      key={offer.id}
                      className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 hover:bg-gray-50 overflow-hidden"
                      data-testid={`offer-row-${offer.id}`}
                    >
                      {/* Image (si existe) - Content Fit */}
                      {offer.image_url && (
                        <div className="w-16 h-16 bg-slate-50 rounded hidden sm:flex items-center justify-center">
                          <img
                            src={offer.image_url}
                            alt={offer.title}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      )}
                      
                      {/* Type Icon */}
                      <div className={`p-2 rounded-lg ${typeInfo.color} hidden sm:flex`}>
                        <TypeIcon className="h-5 w-5" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium truncate">{offer.title}</h3>
                          {offer.offer_code && (
                            <Badge variant="secondary" className="font-mono text-[10px]" data-testid={`offer-code-badge-${offer.id}`}>
                              {offer.offer_code}
                            </Badge>
                          )}
                          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                          {/* Badge de Statut avec code couleur */}
                          <Badge className={STATUS_CONFIG[offer.status]?.color || STATUS_CONFIG.draft.color}>
                            {STATUS_CONFIG[offer.status]?.label || offer.status}
                          </Badge>
                          {offer.badge_text && (
                            <Badge style={{ backgroundColor: offer.badge_color }} className="text-white">
                              {offer.badge_text}
                            </Badge>
                          )}
                          {/* Conditions d'achat linking stats */}
                          {offer.linked_mechanics_count > 0 && (
                            <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-300">
                              {offer.linked_mechanics_count} condition(s) liée(s)
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate max-w-full">{offer.description}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                          {offer.discount_percent && <span>-{offer.discount_percent}%</span>}
                          {offer.franco_threshold && <span>Franco {offer.franco_threshold}€</span>}
                          {offer.gratuite_description && <span className="text-pink-600">🎁 {offer.gratuite_description}</span>}
                          {offer.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {offer.start_date} → {offer.end_date || '∞'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Status + Actions */}
                      <div className="flex items-center gap-2">
                        {offer.is_active ? (
                          <Badge className="bg-green-100 text-green-800">
                            <Eye className="h-3 w-3 mr-1" /> Active
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">
                            <EyeOff className="h-3 w-3 mr-1" /> Inactive
                          </Badge>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(offer)}
                          data-testid={`edit-offer-${offer.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(offer)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-offer-${offer.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <GuardedDialog open={showForm} onOpenChange={setShowForm} isDirty={hasUnsavedChanges} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingOffer ? 'Modifier l\'offre' : 'Nouvelle offre'}
            </DialogTitle>
            <DialogDescription>
              {editingOffer ? 'Modifiez les détails de votre offre' : 'Créez une nouvelle offre promotionnelle'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} onChange={() => setDirty()} className="space-y-6">
            {/* Section: Informations de base */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('basic')}
                className="flex items-center justify-between w-full text-left font-semibold pb-2 border-b"
              >
                <span>Informations de base</span>
                {expandedSections.basic ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {expandedSections.basic && (
                <div className="space-y-4 pt-2">
                  {/* Phase 2: offer_code */}
                  <div className="space-y-2">
                    <Label>Code Offre *</Label>
                    <Input
                      value={formData.offer_code}
                      onChange={(e) => setFormData({...formData, offer_code: e.target.value.toUpperCase().replace(/\s+/g, '-')})}
                      placeholder="ex: PROMO-ETE-2026"
                      disabled={!!editingOffer}
                      data-testid="offer-code-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {editingOffer ? 'Non modifiable après création.' : 'Identifiant technique unique pour cette offre.'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Titre *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Ex: -20% sur la nouvelle collection"
                      required
                      data-testid="offer-title-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Détails de l'offre..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type d'offre</Label>
                      <Select
                        value={formData.type}
                        onValueChange={(newType) => {
                          // Reset des champs non pertinents selon le nouveau type
                          const updates = { type: newType };
                          if (newType !== 'remise' && newType !== 'mixte') {
                            updates.discount_percent = '';
                          }
                          if (newType !== 'franco' && newType !== 'mixte') {
                            updates.franco_threshold = '';
                          }
                          if (newType !== 'goodies' && newType !== 'mixte') {
                            updates.goodies_description = '';
                          }
                          if (newType !== 'gratuite' && newType !== 'mixte') {
                            updates.gratuite_description = '';
                          }
                          setFormData({...formData, ...updates});
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OFFER_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Statut</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(v) => setFormData({...formData, status: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Brouillon</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="archived">Archivée</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                    />
                    <Label>Offre active</Label>
                  </div>
                </div>
              )}
            </div>

            {/* Section: Paramètres */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('params')}
                className="flex items-center justify-between w-full text-left font-semibold pb-2 border-b"
              >
                <span>Paramètres de l'offre</span>
                {expandedSections.params ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {expandedSections.params && (
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(formData.type === 'remise' || formData.type === 'mixte') && (
                      <div className="space-y-2">
                        <Label>Remise (%)</Label>
                        <Input
                          type="number"
                          value={formData.discount_percent}
                          onChange={(e) => setFormData({...formData, discount_percent: e.target.value})}
                          placeholder="20"
                          min="0"
                          max="100"
                        />
                      </div>
                    )}
                    
                    {(formData.type === 'franco' || formData.type === 'mixte') && (
                      <div className="space-y-2">
                        <Label>Franco (€)</Label>
                        <Input
                          type="number"
                          value={formData.franco_threshold}
                          onChange={(e) => setFormData({...formData, franco_threshold: e.target.value})}
                          placeholder="200"
                          min="0"
                        />
                      </div>
                    )}
                    
                    {(formData.type === 'goodies' || formData.type === 'mixte') && (
                      <div className="space-y-2">
                        <Label>Description goodies</Label>
                        <Input
                          value={formData.goodies_description}
                          onChange={(e) => setFormData({...formData, goodies_description: e.target.value})}
                          placeholder="Ex: 1 poster offert"
                        />
                      </div>
                    )}
                    
                    {/* Champ Gratuité - visible UNIQUEMENT si type gratuite ou mixte */}
                    {(formData.type === 'gratuite' || formData.type === 'mixte') && (
                      <div className={`space-y-2 ${formData.type === 'gratuite' ? 'sm:col-span-2 bg-pink-50 p-3 rounded-lg border border-pink-200' : ''}`}>
                        <Label className={formData.type === 'gratuite' ? 'font-semibold text-pink-800' : ''}>
                          Gratuité {formData.type === 'gratuite' && '*'}
                        </Label>
                        <Input
                          value={formData.gratuite_description}
                          onChange={(e) => setFormData({...formData, gratuite_description: e.target.value})}
                          placeholder="Ex: 1 produit offert, Livraison offerte..."
                          maxLength={200}
                          data-testid="gratuite-description-input"
                        />
                        <p className="text-xs text-muted-foreground">Texte libre décrivant la gratuité</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Commandes min/max avec unités */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Commande min.</Label>
                      <Input
                        type="number"
                        value={formData.minimum_order}
                        onChange={(e) => setFormData({...formData, minimum_order: e.target.value})}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unité min.</Label>
                      <Select
                        value={formData.min_order_unit}
                        onValueChange={(v) => setFormData({...formData, min_order_unit: v})}
                      >
                        <SelectTrigger data-testid="min-order-unit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ORDER_UNITS.map(unit => (
                            <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Commande max.</Label>
                      <Input
                        type="number"
                        value={formData.maximum_order}
                        onChange={(e) => setFormData({...formData, maximum_order: e.target.value})}
                        placeholder="∞"
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unité max.</Label>
                      <Select
                        value={formData.max_order_unit}
                        onValueChange={(v) => setFormData({...formData, max_order_unit: v})}
                      >
                        <SelectTrigger data-testid="max-order-unit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ORDER_UNITS.map(unit => (
                            <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Multiple de commande */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Multiple de commande</Label>
                      <Input
                        type="number"
                        value={formData.multiple_order}
                        onChange={(e) => setFormData({...formData, multiple_order: e.target.value})}
                        placeholder="1"
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lien vers l'offre</Label>
                      <Input
                        type="url"
                        value={formData.offer_url}
                        onChange={(e) => setFormData({...formData, offer_url: e.target.value})}
                        placeholder="https://..."
                        data-testid="offer-url-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Section: Dates */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('dates')}
                className="flex items-center justify-between w-full text-left font-semibold pb-2 border-b"
              >
                <span>Période de validité</span>
                {expandedSections.dates ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {expandedSections.dates && (
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label>Date de début</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date de fin</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Section: Affichage CMS */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('display')}
                className="flex items-center justify-between w-full text-left font-semibold pb-2 border-b"
              >
                <span>Affichage (optionnel)</span>
                {expandedSections.display ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {expandedSections.display && (
                <div className="space-y-4 pt-2">
                  <ImageUploadField
                    value={formData.image_url}
                    onChange={(url) => setFormData({...formData, image_url: url})}
                    label="Image de l'offre"
                  />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Texte badge</Label>
                      <Input
                        value={formData.badge_text}
                        onChange={(e) => setFormData({...formData, badge_text: e.target.value})}
                        placeholder="Ex: -20%"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Couleur badge</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={formData.badge_color}
                          onChange={(e) => setFormData({...formData, badge_color: e.target.value})}
                          className="w-12 h-10 p-1"
                        />
                        <Input
                          value={formData.badge_color}
                          onChange={(e) => setFormData({...formData, badge_color: e.target.value})}
                          placeholder="#ef4444"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview badge */}
                  {formData.badge_text && (
                    <div className="pt-2">
                      <Label className="text-xs text-muted-foreground">Aperçu:</Label>
                      <Badge 
                        style={{ backgroundColor: formData.badge_color }} 
                        className="text-white mt-1"
                      >
                        {formData.badge_text}
                      </Badge>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Section: Produits Concernés */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('products')}
                className="flex items-center justify-between w-full text-left font-semibold pb-2 border-b"
              >
                <span className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Produits concernés
                </span>
                {expandedSections.products ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              
              {expandedSections.products && (
                <div className="space-y-4 pt-2">
                  {/* Mode de sélection */}
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="partner-apply-all"
                        checked={formData.apply_to_all_products}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          apply_to_all_products: checked,
                          product_ids: checked ? [] : formData.product_ids
                        })}
                      />
                      <Label htmlFor="partner-apply-all" className="cursor-pointer font-normal">
                        Appliquer à tous mes produits
                      </Label>
                    </div>
                  </div>
                  
                  {/* Sélection de produits spécifiques */}
                  {!formData.apply_to_all_products && (
                    <div className="space-y-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Rechercher un produit..."
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                          className="pl-10"
                        />
                      </div>
                      
                      {/* Produits sélectionnés */}
                      {formData.product_ids.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formData.product_ids.map(productId => {
                            const product = products.find(p => p.id === productId);
                            return (
                              <Badge 
                                key={productId} 
                                variant="secondary"
                                className="flex items-center gap-1 pr-1"
                              >
                                {product?.intitule || product?.name || productId}
                                <button
                                  type="button"
                                  onClick={() => setFormData({
                                    ...formData,
                                    product_ids: formData.product_ids.filter(id => id !== productId)
                                  })}
                                  className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Liste des produits disponibles */}
                      <div className="border rounded-lg max-h-40 overflow-y-auto">
                        {products
                          .filter(p => {
                            const productName = (p.intitule || p.name || '').toLowerCase();
                            return productName.includes(productSearch.toLowerCase()) &&
                              !formData.product_ids.includes(p.id);
                          })
                          .slice(0, 15)
                          .map(product => (
                            <div
                              key={product.id}
                              className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => setFormData({
                                ...formData,
                                product_ids: [...formData.product_ids, product.id]
                              })}
                            >
                              {product.visuels?.[0] ? (
                                <img 
                                  src={product.visuels[0]} 
                                  alt="" 
                                  className="w-8 h-8 object-cover rounded"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                                  <Package className="h-4 w-4 text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{product.intitule || product.name}</p>
                              </div>
                              <Plus className="h-4 w-4 text-gray-400" />
                            </div>
                          ))}
                        
                        {products.filter(p => {
                          const productName = (p.intitule || p.name || '').toLowerCase();
                          return productName.includes(productSearch.toLowerCase()) &&
                            !formData.product_ids.includes(p.id);
                        }).length === 0 && (
                          <div className="p-4 text-center text-muted-foreground text-sm">
                            Aucun produit trouvé
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-muted-foreground">
                        {formData.product_ids.length} produit(s) sélectionné(s)
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button 
                type="button" 
                variant="secondary"
                onClick={() => {
                  // Preview: ouvre une modal ou nouvel onglet avec les données actuelles
                  if (editingOffer) {
                    window.open(`/dashboard/offer-preview/${editingOffer.id}`, '_blank');
                  } else {
                    // Mode création: prévisualisation locale via state
                    const previewData = encodeURIComponent(JSON.stringify(formData));
                    window.open(`/dashboard/offer-preview/new?data=${previewData}`, '_blank');
                  }
                }}
                data-testid="preview-offer-button"
              >
                <Eye className="h-4 w-4 mr-2" />
                Prévisualiser
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-offer-button">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : editingOffer ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
      </GuardedDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette offre ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer "{deleteConfirm?.title}" ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardWrapper>
  );
};

export default PartnerOffers;
